import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Backdrop,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { API_URL } from "../../utils/config";
import { canSendReport } from "../../utils/auth";
import { v4 as uuidv4 } from "uuid";

const RATE_CHANGE_FIELDS = [
  { key: "state", label: "State" },
  { key: "utility", label: "Utility" },
  { key: "utility_code", label: "Utility Code" },
  { key: "class", label: "Class" },
  { key: "commodity", label: "Commodity" },
  { key: "previous_rate", label: "Previous Rate" },
  { key: "new_rate", label: "New Rate" },
  { key: "created_ts", label: "Date Change Detected" },
  { key: "effective_start_date", label: "Effective Start Date" },
  { key: "effective_end_date", label: "Effective End Date" },
];

const emptyRow = () =>
  RATE_CHANGE_FIELDS.reduce(
    (acc, { key }) => ({ ...acc, [key]: "" }),
    { id: uuidv4(), direction: "" }
  );

const UP_DIRECTION_PATTERN = /[▲△↑⬆🔺]|^up$|^increase$/i;
const DOWN_DIRECTION_PATTERN = /[▼▽↓⬇🔻]|^down$|^decrease$/i;

const getDirectionColor = (value) => {
  if (UP_DIRECTION_PATTERN.test(value)) return "success.main";
  if (DOWN_DIRECTION_PATTERN.test(value)) return "error.main";
  return "text.primary";
};

const renderDirection = (direction) => {
  const value = String(direction ?? "").trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value) || value.startsWith("data:image")) {
    return (
      <Box
        component="img"
        src={value}
        alt="direction"
        sx={{ width: 24, height: 24, display: "block", mx: "auto" }}
      />
    );
  }
  return (
    <Box
      component="span"
      sx={{
        fontSize: 20,
        lineHeight: 1,
        display: "block",
        textAlign: "center",
        color: getDirectionColor(value),
      }}
    >
      {value}
    </Box>
  );
};

const SendPTCReportConfirm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isErrorMessage, setIsErrorMessage] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const allowed = await canSendReport();
        setShowButton(allowed);
      } catch (error) {
        console.error("Error checking user:", error);
        setShowButton(false);
      }
    };
    checkUser();
  }, []);

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        const session = await fetchAuthSession();
        if (!session.tokens?.accessToken) {
          throw new Error("No access token available");
        }
        const token = session.tokens.accessToken.toString();
        const res = await fetch(`${API_URL}/ptc/get_report_rate_changes_raw`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        const raw =
          data.rate_changes_raw ?? data.response ?? (Array.isArray(data) ? data : []);
        const normalized = raw.length
          ? raw.map((item) => ({ id: uuidv4(), ...item }))
          : [emptyRow()];
        setRows(normalized);
      } catch (error) {
        console.error("Error fetching report data:", error);
        setRows([emptyRow()]);
        setMessage("Failed to load report data. You can still edit and send.");
        setIsErrorMessage(true);
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchReportData();
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleCellChange = (rowId, fieldKey, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, [fieldKey]: value } : row
      )
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const handleDeleteRow = (rowId) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== rowId);
      return next.length ? next : [emptyRow()];
    });
  };

  const buildRateChangesRaw = () =>
    rows.map((row) => {
      const item = RATE_CHANGE_FIELDS.reduce((acc, { key }) => {
        acc[key] = String(row[key] ?? "").trim();
        return acc;
      }, {});
      const direction = String(row.direction ?? "").trim();
      if (direction) item.direction = direction;
      return item;
    });

  const normalizeEmail = (value) =>
    value
      ?.split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ") ?? "";

  const handleSend = async () => {
    const trimmedEmail = normalizeEmail(email);
    if (!trimmedEmail) {
      setMessage("Please enter at least one email address");
      setIsErrorMessage(true);
      setSnackbarOpen(true);
      return;
    }

    setSending(true);
    try {
      const allowed = await canSendReport();
      if (!allowed) {
        throw new Error("You do not have permission to send this report");
      }

      const session = await fetchAuthSession();
      if (!session.tokens?.accessToken) {
        throw new Error("No access token available");
      }
      const token = session.tokens.accessToken.toString();
      const rate_changes_raw = buildRateChangesRaw();

      const response = await fetch(`${API_URL}/ptc/send_report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: trimmedEmail,
          rate_changes_raw,
        }),
      });

      const data = await response.json().catch(() => ({}));
      const isError = data.status !== "1";
      setIsErrorMessage(isError);
      setMessage(
        data.message ||
          (isError ? "Failed to send, please try again later" : "Report sent successfully!")
      );
      setSnackbarOpen(true);

      if (!isError) {
        setTimeout(() => navigate(-1), 800);
      }
    } catch (error) {
      console.error("Error sending report:", error);
      setMessage(error.message || "Failed to send, please try again later");
      setIsErrorMessage(true);
      setSnackbarOpen(true);
    } finally {
      setSending(false);
    }
  };

  if (!showButton) {
    navigate(-1);
    return null;
  }

  return (
    <Box className="w-full min-h-[100vh] bg-[#EAF4FC]">
      <Header />
      <div className="w-full flex justify-center pt-10 pb-10">
        <div className="w-[92%] bg-white rounded shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-6">
            Send PTC Rate Change Report — Confirm & Send
          </h2>

          <Box className="mb-4">
            <TextField
              fullWidth
              label="Email"
              type="text"
              variant="outlined"
              multiline
              minRows={1}
              maxRows={15}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => setEmail(normalizeEmail(e.target.value))}
              disabled={sending}
              placeholder="Separate multiple emails with comma"
            />
          </Box>

          <Box className="mb-4 flex justify-between items-center">
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              disabled={loading || sending}
            >
              Add Row
            </Button>
          </Box>

          <TableContainer component={Paper} className="mb-6">
            {loading ? (
              <div className="flex justify-center py-16">
                <CircularProgress />
              </div>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 600, width: 56 }}>
                      Direction
                    </TableCell>
                    {RATE_CHANGE_FIELDS.map(({ key, label }) => (
                      <TableCell
                        key={key}
                        align="left"
                        sx={{ fontWeight: 600, ...(key === "utility" ? { minWidth: 320 } : {}) }}
                      >
                        {label}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center" padding="checkbox">
                        {renderDirection(row.direction)}
                      </TableCell>
                      {RATE_CHANGE_FIELDS.map(({ key }) => (
                        <TableCell
                          key={key}
                          align="left"
                          padding="none"
                          sx={key === "utility" ? { minWidth: 320 } : undefined}
                        >
                          <Tooltip
                            title={String(row[key] ?? "")}
                            placement="top"
                            arrow
                            disableHoverListener={!row[key]}
                          >
                            <TextField
                              size="small"
                              fullWidth
                              value={row[key] ?? ""}
                              onChange={(e) =>
                                handleCellChange(row.id, key, e.target.value)
                              }
                              disabled={sending}
                              variant="outlined"
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
                            />
                          </Tooltip>
                        </TableCell>
                      ))}
                      <TableCell align="center" padding="none">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRow(row.id)}
                          disabled={sending || rows.length <= 1}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          <Box className="flex justify-center gap-4">
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSend}
              disabled={loading || sending}
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </Box>
        </div>
      </div>

      <Backdrop
        sx={(theme) => ({
          color: "#fff",
          zIndex: theme.zIndex.modal + 1,
        })}
        open={sending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={isErrorMessage ? "error" : "success"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SendPTCReportConfirm;
